import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { usePaginatedQuery } from 'react-query';
import { useNavigation } from 'react-navi';
import {
  Box,
  Table,
  Button,
  TableFiltersContainer,
  tableFilterCss,
  PaginationSize,
  usePagination,
  themeUtils as tu,
} from '@zen-common/components-base';
import { textUtils, assetUtils, numberUtils } from '@zen/common-utils';
import {
  NamingAssetStore,
  SettingsStore,
  StorageUpdaterStore,
  TitleContentLayout,
} from '@zen/common-app-parts';
import { useLoadingOnUserGesture } from '@zen/common-react-hooks';
import { isZP } from '@zen/common-utils/lib/utils/assetUtils';
import { useDbClient } from '../../components/StoreProviders/components/DbClientContext';
import * as priceUtils from '../../utils/priceUtils';
import Pair from '../../components/Pair';
import PATHS from '../../paths';

const STORAGE_KEY = 'dex-traded-pairs-table-page-size';

export default function TopPairs() {
  const { findTopPairs, subscribe, unsubscribe } = useDbClient();
  const {
    state: { chain },
  } = SettingsStore.useStore();
  const [pairs, setPairs] = useState([]);
  const [itemsCount, setItemsCount] = useState(0);
  const storage = StorageUpdaterStore.useStorage();

  const { state: naming } = NamingAssetStore.useStore();

  const isMobile = document.body.clientWidth < tu.breakpoints.xl.replace('px', '');

  const pagination = usePagination({
    manual: true,
    itemsCount,
    initialPageSize: storage.get(STORAGE_KEY) || undefined, // prevent passing null
  });

  const skip = pagination.page * pagination.pageSize;
  const take = pagination.pageSize;

  const { isFetching, latestData } = usePaginatedQuery(
    ['dex-traded-pairs', { skip, take, chain }],
    (key, params) => findTopPairs(params).catch(console.error),
    { refetchOnWindowFocus: false }
  );

  const loading = useLoadingOnUserGesture({
    isFetching,
    latestData,
    page: pagination.page,
    pageSize: pagination.pageSize,
  });

  // remember selected page size
  StorageUpdaterStore.useUpdater([
    {
      key: STORAGE_KEY,
      value: pagination.pageSize,
    },
  ]);

  // data is set from more than 1 place
  const setData = useCallback(
    (data) => {
      setPairs(data.items.map((item, index) => ({ ...item, place: skip + index + 1 })));
      setItemsCount(data.count);
    },
    [skip]
  );

  // set the data on query data change
  useEffect(() => {
    if (latestData) {
      setData(latestData);
    }
  }, [latestData, setData]);

  // subscribe for changes
  useEffect(() => {
    const id = subscribe({
      method: 'findTopPairs',
      payload: { skip, take },
      handler: setData,
    });

    return () => unsubscribe(id);
  }, [setData, skip, subscribe, take, unsubscribe]);

  const tableColumns = [
    {
      Header: '#',
      accessor: 'place',
      minWidth: 20,
      Cell: ({ value }) => textUtils.addPrecedingZero(value),
    },
    {
      Header: 'PAIR',
      accessor: '',
      minWidth: isMobile ? 385 : 285,
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => <Pair base={original.base} quote={original.quote} naming={naming} />,
    },
    {
      Header: 'LAST PRICE',
      accessor: 'lastPrice',
      minWidth: 150,
      Cell: ({ value }) => (value === 0 ? '-' : priceUtils.toDisplay(value)),
    },
    {
      Header: 'OPEN BUY ORDERS',
      accessor: 'buyOrders',
      minWidth: 150,
      Cell: ({ value }) => numberUtils.toDisplay(value),
    },
    {
      Header: 'OPEN SELL ORDERS',
      accessor: 'sellOrders',
      minWidth: 150,
      Cell: ({ value }) => numberUtils.toDisplay(value),
    },
    {
      Header: 'Total Orders',
      accessor: 'executions',
      minWidth: 150,
      Cell: ({ value }) => numberUtils.toDisplay(value),
    },
    {
      Header: 'ACTIONS',
      accessor: '',
      className: 'align-right',
      headerClassName: 'align-right',
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => <TradeButton {...original} />,
    },
  ];

  return (
    <TitleContentLayout dark noPadding title="Traded Pairs">
      <MainContainer>
        <TableFiltersContainer>
          <PaginationSize {...pagination} css={tableFilterCss} width={['100%', 'auto']} />
        </TableFiltersContainer>
        <Table
          data={pairs}
          loading={loading}
          columns={tableColumns}
          resizable={false}
          sortable={false}
          minRows={0}
          {...pagination}
        />
      </MainContainer>
    </TitleContentLayout>
  );
}

const MainContainer = styled(Box)`
  position: relative;
`;

const TradeButton = ({ base, quote }) => {
  const { navigate } = useNavigation();
  const onClick = () => {
    const baseQuote = isZP(base) ? { base: quote, quote: base } : { base, quote };
    const baseFormatted = assetUtils.getAssetRepresentationId(baseQuote.base);
    const quoteFormatted = assetUtils.getAssetRepresentationId(baseQuote.quote);
    navigate(`${PATHS.ORDER_BOOK}?base=${baseFormatted}&quote=${quoteFormatted}`);
  };
  return (
    <Button sizeVariant="small" onClick={onClick}>
      Trade
    </Button>
  );
};
